// frontend/src/site-config.ts

export interface SiteVideo {
  id: string;
  titleKey: string;          // i18n key under 'user-dashboard' namespace
  descriptionKey: string;    // i18n key under 'user-dashboard' namespace
  url: string;               // YouTube/Vimeo/MP4 URL
  thumbnailUrl?: string;     // Optional custom thumbnail
  duration?: string;         // Display string, e.g. '3:42'
}

export interface SiteConfig {
  siteId: string;
  displayName: string;
  // Full canonical URL for this build's domain, e.g. 'https://aiacceleratorcoach.com'.
  // Used for CTA links/copy so no brand domain is hardcoded per site.
  siteUrl: string;
  // Which accelerator category slugs this site shows.
  // Use ['*'] for "all categories" (the main site).
  acceleratorCategories: string[] | ['*'];
  // Which platform-specific route prefixes to include (e.g., 'sewing-fashion').
  // Use ['*'] for "all prefixes" (the main site).
  platformRoutePrefixes: string[] | ['*'];
  // Optional branding overrides
  logoPath?: string;
  themeColor?: string;
  faviconPath?: string;
  // Site-specific videos shown on the dashboard
  videos?: SiteVideo[];
  // Default language for this domain's bare-path redirect (omit → 'en').
  defaultLocale?: 'en' | 'es';
}

// Master list of all 33 accelerator category slugs
export const ALL_ACCELERATOR_CATEGORIES = [
  'ai-artist-studio', 'ai-automotive', 'ai-black-men', 'ai-black-women',
  'ai-christian', 'ai-culinary', 'ai-disability', 'ai-engineering',
  'ai-environment', 'ai-film-studio', 'ai-furniture-making',
  'ai-hotel-hospitality', 'ai-indigenous', 'ai-jewish', 'ai-lgbtq',
  'ai-latino', 'ai-low-income', 'ai-medical', 'ai-minority',
  'ai-music-studio', 'ai-muslim', 'ai-next-step', 'ai-panama', 'ai-real-estate',
  'ai-rural', 'ai-stem', 'ai-senior', 'ai-sewing-fashion',
  'ai-single-parent', 'ai-university-student', 'ai-veterans',
  'ai-vocational', 'ai-wellness-beauty', 'ai-women',
] as const;

// Master list of all platform-specific route prefixes
export const ALL_PLATFORM_PREFIXES = [
  'sewing-fashion',
  // Add more as platform-specific tool subfolders are created:
  // 'film', 'culinary', etc.
] as const;

/**
 * Site registry. To add a new standalone domain:
 * 1. Add an entry here
 * 2. Add a .env.production.<siteId> file
 * 3. Add the site to the Terraform sites variable in .tfvars
 * 4. Run build-sites.sh and terraform apply
 */
export const SITE_CONFIGS: Record<string, SiteConfig> = {
  main: {
    siteId: 'main',
    displayName: 'AI Accelerator Coach',
    // Must stay equal to main's custom_domain in backend/src/config/site_map.py
    // (forward map <-> reverse map pair). SITE_DOMAIN feeds the i18n {{siteUrl}}
    // token, so a value that disagrees with DNS shows users a dead hostname.
    siteUrl: 'https://aiacceleratorcoach.com',
    acceleratorCategories: ['*'],
    platformRoutePrefixes: ['*'],
    videos: [
      {
        id: 'welcome',
        titleKey: 'videos.items.welcome.title',
        descriptionKey: 'videos.items.welcome.description',
        url: 'https://www.youtube.com/watch?v=PLACEHOLDER',
        duration: '4:12',
      },
      {
        id: 'lexicon-walkthrough',
        titleKey: 'videos.items.lexiconWalkthrough.title',
        descriptionKey: 'videos.items.lexiconWalkthrough.description',
        url: 'https://www.youtube.com/watch?v=PLACEHOLDER2',
        duration: '6:30',
      },
      {
        id: 'profile-tips',
        titleKey: 'videos.items.profileTips.title',
        descriptionKey: 'videos.items.profileTips.description',
        url: 'https://vimeo.com/PLACEHOLDER3',
        duration: '5:15',
      },
    ],
  },
  sewing: {
    siteId: 'sewing',
    displayName: 'AI Sewing & Fashion Accelerator',
    siteUrl: 'https://aisewingfashionaccelerator.com',
    acceleratorCategories: ['ai-sewing-fashion'],
    platformRoutePrefixes: ['sewing-fashion'],
    videos: [
      {
        id: 'sewing-intro',
        titleKey: 'videos.items.sewingIntro.title',
        descriptionKey: 'videos.items.sewingIntro.description',
        url: 'https://www.youtube.com/watch?v=SEW_PLACEHOLDER',
        duration: '3:45',
      },
    ],
  },
  film: {
    siteId: 'film',
    displayName: 'AI Cinema Accelerator',
    siteUrl: 'https://aicinemaaccelerator.com',
    acceleratorCategories: ['ai-film-studio'],
    platformRoutePrefixes: [],  // No platform-specific subfolder yet
    videos: [
      {
        id: 'film-intro',
        titleKey: 'videos.items.filmIntro.title',
        descriptionKey: 'videos.items.filmIntro.description',
        url: 'https://www.youtube.com/watch?v=FILM_PLACEHOLDER',
        duration: '5:00',
      },
    ],
  },
  medical: {
    siteId: 'medical',
    displayName: 'AI Medical Accelerator',
    siteUrl: 'https://aimedicalaccelerator.com',
    acceleratorCategories: ['ai-medical'],
    platformRoutePrefixes: [],  // No platform-specific subfolder
  },
  panama: {
    siteId: 'panama',
    displayName: 'AI Emprende Panamá',
    siteUrl: 'https://aiemprendepanama.com',
    acceleratorCategories: ['ai-panama'],
    platformRoutePrefixes: [],  // No platform-specific subfolder
    defaultLocale: 'es',        // Spanish-first site
  },
  'black-women': {
    siteId: 'black-women',
    displayName: 'AI Black Women Accelerator',
    siteUrl: 'https://AiBlackWomenAccelerator.com',
    acceleratorCategories: ['ai-black-women'],
    platformRoutePrefixes: [],  // No platform-specific subfolder
  },
  // ─── Examples of how easy it is to add more ───
  // culinary: {
  //   siteId: 'culinary',
  //   displayName: 'AI Culinary Accelerator',
  //   acceleratorCategories: ['ai-culinary'],
  //   platformRoutePrefixes: [],
  // },
  // medical: {
  //   siteId: 'medical',
  //   displayName: 'AI Medical Accelerator',
  //   acceleratorCategories: ['ai-medical'],
  //   platformRoutePrefixes: [],
  // },
  // veterans: {
  //   siteId: 'veterans',
  //   displayName: 'AI Veterans Accelerator',
  //   acceleratorCategories: ['ai-veterans'],
  //   platformRoutePrefixes: [],
  // },
};

// ─── Runtime resolution ───

const rawSiteId = import.meta.env.VITE_SITE_ID || 'main';
export const CURRENT_SITE: SiteConfig = SITE_CONFIGS[rawSiteId] ?? SITE_CONFIGS.main;

/** Full canonical URL for this build, e.g. 'https://aiacceleratorcoach.com'. Use as link href. */
export const SITE_URL: string = CURRENT_SITE.siteUrl;
/** Bare domain for display, e.g. 'aiacceleratorcoach.com'. Use as i18n {{siteUrl}} value. */
export const SITE_DOMAIN: string = SITE_URL.replace(/^https?:\/\//, '');

/** Default locale for this site's bare-path redirect, e.g. '/es' for a Spanish-first domain (omit → 'en'). */
export const DEFAULT_LOCALE: 'en' | 'es' = CURRENT_SITE.defaultLocale ?? 'en';

/** Does this site show a given accelerator category? */
export function isAcceleratorCategoryVisible(slug: string): boolean {
  if (CURRENT_SITE.acceleratorCategories[0] === '*') return true;
  return CURRENT_SITE.acceleratorCategories.includes(slug);
}

/**
 * The accelerator "catalog" slug to link to for the CURRENT site.
 * - Main / all-categories site (`['*']`) → the master `ai-accelerators-table`.
 * - A single/limited-vertical site → its (first) visible category page, e.g.
 *   Panama → `ai-panama`, Sewing → `ai-sewing-fashion`. This mirrors the
 *   "Accelerator Programs" sidebar, which hides `ai-accelerators-table` on
 *   single-vertical sites and shows only that site's category.
 */
export function getSiteCatalogSlug(): string {
  const cats = CURRENT_SITE.acceleratorCategories;
  if (!cats || cats.length === 0 || cats[0] === '*') return 'ai-accelerators-table';
  return cats[0];
}

/** Does this site show a given platform-specific route prefix? */
export function isPlatformRouteVisible(prefix: string): boolean {
  if (CURRENT_SITE.platformRoutePrefixes[0] === '*') return true;
  return CURRENT_SITE.platformRoutePrefixes.includes(prefix);
}
